import { createContext, useContext, useEffect, useState, useRef } from "react";
import { getAllDowntimeEvents } from "@/api/downtime-api.js";
import { isRequestAborted } from "@/api/api-utils.js";
import {
  transformApiResponseToDowntimeEventSummaries,
  enrichDowntimeEventsWithAssetData,
} from "@/lib/downtime-data-utils.js";
import { getDateOffset } from "@/lib/datetime-utils.js";
import { useDataLoaderContext } from "./DataLoaderContext.jsx";
import { useAssets } from "./AssetContext.jsx";
import { useSessionTenant } from "./SessionTenantContext.jsx";

/**
 * @typedef {Object} DowntimeEvent
 * @property {string} id - Event ID
 * @property {boolean} active - Whether the event is currently active
 * @property {number} duration - Duration in minutes
 * @property {string} dateTime - Event date/time
 * @property {string} relatedAlarm - Related alarm description
 * @property {number} dtHours - Downtime hours
 * @property {boolean|null} mechanical - Whether the event is mechanical (true), non-mechanical (false), or unknown (null)
 */

/**
 * @typedef {Object} DowntimeAsset
 * @property {number} assetId - Non-nullable int from downtime API (`AssetId`); same numeric value as fleet `AssetId` (`asset.id` is stringified in AssetContext)
 * @property {string} assetNumber - Display asset number from fleet enrichment (not unique)
 * @property {string|undefined} assetLocation - Asset location
 * @property {string|undefined} vendor - Vendor name
 * @property {string|undefined} status - Asset status from AssetContext
 * @property {DowntimeEvent[]} events - Array of downtime events
 * @property {number} totalDTHours - Total downtime hours
 * @property {number} mechanicalDTHours - Mechanical downtime hours
 * @property {number} nonMechanicalDTHours - Non-mechanical downtime hours
 * @property {number} eventCount - Number of events
 */

/**
 * @typedef {Object} DowntimeContextType
 * @property {DowntimeAsset[]} downtimeEvents - Array of downtime events grouped by asset
 * @property {boolean} isLoading - Loading state
 * @property {string|null} error - Error message if any
 * @property {Date|null} lastUpdatedTime - Timestamp of last data update
 * @property {() => Promise<void>} refetch - Function to refetch data
 * @property {() => Promise<void>} fetchDowntimeEvents - Function to fetch downtime events (always 30 days)
 */

const DowntimeContext = createContext(
  /** @type {DowntimeContextType|null} */ (null)
);

/**
 * Hook to use the downtime context
 * @returns {DowntimeContextType} Downtime context value
 * @throws {Error} If used outside of DowntimeProvider
 */
export const useDowntime = () => {
  const context = useContext(DowntimeContext);
  if (!context) {
    throw new Error("useDowntime must be used within a DowntimeProvider");
  }
  return context;
};

/**
 * Downtime Provider component that provides global downtime state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Downtime provider component
 */
export function DowntimeProvider({ children }) {
  /** @type {[DowntimeAsset[], Function]} */
  const [downtimeEvents, setDowntimeEvents] = useState([]);

  /** @type {[boolean, Function]} */
  const [isLoading, setIsLoading] = useState(true);

  /** @type {[string|null, Function]} */
  const [error, setError] = useState(null);

  /** @type {[Date|null, Function]} */
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);

  // Get the shared data loader context
  const { triggerCount = 0 } = useDataLoaderContext();
  const { tenantRefreshKey } = useSessionTenant();

  // Get assets from AssetContext for enrichment
  const { assets } = useAssets();

  const enrichmentAttempted = useRef(false);
  const tenantRefreshFetchRef = useRef(false);

  /**
   * Fetch downtime event data from the downtime API
   * Always fetches 30 days of data for in-memory filtering
   * @returns {Promise<void>}
   */
  const fetchDowntimeEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const newestReportingDate = new Date().toISOString(); // API is UTC
      const oldestReportingDate = getDateOffset(newestReportingDate, "30d");

      const apiResponse = await getAllDowntimeEvents(
        oldestReportingDate,
        newestReportingDate
      );
      const transformedDowntimes = transformApiResponseToDowntimeEventSummaries(
        apiResponse,
        new Date(oldestReportingDate),
        new Date(newestReportingDate)
      );

      setDowntimeEvents(
        Array.isArray(transformedDowntimes) ? transformedDowntimes : []
      );

      enrichmentAttempted.current = false;
      // Update the last updated time
      setLastUpdatedTime(new Date());
    } catch (err) {
      if (isRequestAborted(err)) return;
      if (tenantRefreshFetchRef.current && err?.response?.status === 401) {
        return;
      }
      console.error("Error loading downtime events from API:", err);
      setError("Failed to load downtime events data");
      setDowntimeEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refetch downtime events (always 30 days)
   * @returns {Promise<void>}
   */
  const refetch = async () => {
    await fetchDowntimeEvents();
  };

  // Listen for triggerLoad from DataLoaderContext
  useEffect(() => {
    if (triggerCount > 0) {
      fetchDowntimeEvents();
    }
  }, [triggerCount]);

  useEffect(() => {
    if (tenantRefreshKey > 0) {
      tenantRefreshFetchRef.current = true;
      fetchDowntimeEvents().finally(() => {
        tenantRefreshFetchRef.current = false;
      });
    }
  }, [tenantRefreshKey]);

  // Enrich downtime events when both downtime events and assets are available
  useEffect(() => {
    if (
      downtimeEvents.length > 0
      && assets.length > 0
      && !enrichmentAttempted.current
    ) {
      const needsEnrichment = downtimeEvents.some(
        (event) => !event.vendor || !event.status
      );

      if (needsEnrichment) {
        const enrichedDowntimes = enrichDowntimeEventsWithAssetData(
          downtimeEvents,
          assets
        );
        setDowntimeEvents(enrichedDowntimes);
        enrichmentAttempted.current = true;
      } else {
        enrichmentAttempted.current = true;
      }
    }
  }, [assets, downtimeEvents]);

  /** @type {DowntimeContextType} */
  const value = {
    downtimeEvents,
    isLoading,
    error,
    lastUpdatedTime,
    refetch,
    fetchDowntimeEvents,
  };

  return (
    <DowntimeContext.Provider value={value}>
      {children}
    </DowntimeContext.Provider>
  );
}
